import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, TrendingUp } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = ({ user, setUser }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    fetchPost();
    // Allow time for user state to settle
    const timer = setTimeout(() => setUserLoading(false), 100);
    return () => clearTimeout(timer);
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog-posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-post-container">
        {!userLoading && user && <Navigation user={user} setUser={setUser} />}
        {!userLoading && !user && (
          <div className="public-header">
            <div className="header-content">
              <h1 onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>baad.tv</h1>
              <Button onClick={() => navigate('/')} style={{ backgroundColor: '#E62F88' }}>
                Sign In
              </Button>
            </div>
          </div>
        )}
        <div className="loading-state">
          <p>Loading post...</p>
        </div>
        <style jsx>{`
          .blog-post-container {
            min-height: 100vh;
            background: #121212;
          }
          .loading-state {
            text-align: center;
            padding: 3rem;
            color: #b0b0b0;
          }
        `}</style>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-post-container">
        {!userLoading && user && <Navigation user={user} setUser={setUser} />}
        {!userLoading && !user && (
          <div className="public-header">
            <div className="header-content">
              <h1 onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>baad.tv</h1>
              <Button onClick={() => navigate('/')} style={{ backgroundColor: '#E62F88' }}>
                Sign In
              </Button>
            </div>
          </div>
        )}
        <div className="error-state">
          <h2>Post not found</h2>
          <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
        </div>
        <style jsx>{`
          .blog-post-container {
            min-height: 100vh;
            background: #121212;
          }
          .error-state {
            text-align: center;
            padding: 3rem;
            color: #ffffff;
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`${post.title} | baad.tv Blog`}
        description={post.excerpt}
        url={`/blog/${post.slug}`}
        type="article"
        publishedTime={post.created_at}
      />
      <div className="blog-post-container">
        {!userLoading && user && <Navigation user={user} setUser={setUser} />}
        {!userLoading && !user && (
          <div className="public-header">
            <div className="header-content">
              <h1 onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>baad.tv</h1>
              <Button onClick={() => navigate('/')} style={{ backgroundColor: '#E62F88' }}>
                Sign In
              </Button>
            </div>
          </div>
        )}
        
        <div className="blog-post-content">
          <Button 
            onClick={() => navigate('/blog')}
            variant="ghost"
            className="back-button"
          >
            <ArrowLeft size={20} />
            Back to Blog
          </Button>

          <article className="post-article">
            <header className="post-header">
              <h1>{post.title}</h1>
              <div className="post-meta">
                <span className="meta-item">
                  <Calendar size={18} />
                  {formatDate(post.created_at)}
                </span>
                <span className="meta-item">
                  <TrendingUp size={18} />
                  Based on {post.stats.total_reviews} community reviews
                </span>
              </div>
              <div className="featured-shows">
                <strong>Featured Shows:</strong>
                <div className="show-badges">
                  {post.stats.top_shows.map((show, idx) => (
                    <span key={idx} className="show-badge">{show}</span>
                  ))}
                </div>
              </div>
            </header>

            <div className="post-body">
              {post.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            <footer className="post-footer">
              <div className="cta-section">
                <h3>Join the Flock! 🐑</h3>
                <p>Track your favorite shows, share reviews, and see what your friends are watching.</p>
                <Button 
                  onClick={() => navigate('/')}
                  style={{ backgroundColor: '#E62F88', marginTop: '1rem' }}
                >
                  Get Started - It's Free!
                </Button>
              </div>
            </footer>
          </article>
        </div>

        <style jsx>{`
          .blog-post-container {
            min-height: 100vh;
            background: #121212;
          }

          .public-header {
            background: #1a1a1a;
            border-bottom: 1px solid #333333;
            padding: 1rem 0;
          }

          .header-content {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 2rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .header-content h1 {
            color: #E62F88;
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0;
          }

          .blog-post-content {
            max-width: 800px;
            margin: 0 auto;
            padding: 2rem;
          }

          .back-button {
            color: #E62F88;
            margin-bottom: 2rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .post-article {
            background: #1a1a1a;
            border: 1px solid #333333;
            border-radius: 8px;
            padding: 3rem;
          }

          .post-header {
            margin-bottom: 3rem;
            padding-bottom: 2rem;
            border-bottom: 2px solid #333333;
          }

          .post-header h1 {
            font-size: 2.5rem;
            font-weight: 700;
            color: #ffffff;
            line-height: 1.2;
            margin-bottom: 1.5rem;
          }

          .post-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 2rem;
            color: #b0b0b0;
            font-size: 0.95rem;
            margin-bottom: 1.5rem;
          }

          .meta-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .featured-shows {
            padding: 1.5rem;
            background: #2a2a2a;
            border-radius: 8px;
            border: 1px solid #333333;
          }

          .featured-shows strong {
            color: #E62F88;
            display: block;
            margin-bottom: 1rem;
            font-size: 1.1rem;
          }

          .show-badges {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem;
          }

          .show-badge {
            background: #E62F88;
            color: #000000;
            padding: 0.5rem 1rem;
            border-radius: 20px;
            font-size: 0.9rem;
            font-weight: 600;
          }

          .post-body {
            color: #e0e0e0;
            line-height: 1.8;
            font-size: 1.1rem;
          }

          .post-body p {
            margin-bottom: 1.5rem;
          }

          .post-footer {
            margin-top: 3rem;
            padding-top: 2rem;
            border-top: 2px solid #333333;
          }

          .cta-section {
            text-align: center;
            padding: 2rem;
            background: #2a2a2a;
            border-radius: 8px;
            border: 2px solid #E62F88;
          }

          .cta-section h3 {
            color: #E62F88;
            font-size: 1.5rem;
            margin-bottom: 0.5rem;
          }

          .cta-section p {
            color: #b0b0b0;
            margin-bottom: 0;
          }

          @media (max-width: 768px) {
            .blog-post-content {
              padding: 1rem;
            }

            .post-article {
              padding: 1.5rem;
            }

            .post-header h1 {
              font-size: 1.75rem;
            }

            .post-body {
              font-size: 1rem;
            }

            .post-meta {
              flex-direction: column;
              gap: 0.75rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default BlogPost;
